import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * ClientAlertsToggle
 *
 * A recruiter-facing control (interview stages tab, left of Manage Stages) that
 * turns OFF the notifications sent to client accounts for THIS search. Clicking
 * the bell opens a small menu of per-type toggles, since a recruiter may want to
 * mute only stage changes, only new-candidate alerts, or everything.
 *
 * Backed by GET/PUT /api/pipelines/:pipelineId/client-notification-settings.
 */
const TYPE_LABELS = [
  { key: 'stage_change', label: 'Interview stage changes' },
  { key: 'new_candidate', label: 'New candidate presented' },
];

const ClientAlertsToggle = ({ pipelineId }) => {
  const [settings, setSettings] = useState(null); // { stage_change, new_candidate }
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rememberDefault, setRememberDefault] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // Position the menu with fixed coordinates from the button, so it escapes the
  // tab bar's overflow-x-auto (which was clipping it and adding vertical scroll)
  // and any parent stacking context (which was trapping the z-index).
  const openMenu = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  };

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!pipelineId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/pipelines/${pipelineId}/client-notification-settings`, { headers: authHeaders() });
        if (res.ok) { const d = await res.json(); setSettings(d.settings); }
        else setSettings({ stage_change: true, new_candidate: true });
      } catch { setSettings({ stage_change: true, new_candidate: true }); }
    })();
  }, [pipelineId]);

  const save = async (next) => {
    setSaving(true);
    setSettings(next); // optimistic
    try {
      const res = await fetch(`${API_BASE_URL}/api/pipelines/${pipelineId}/client-notification-settings`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ settings: next, remember_as_default: rememberDefault }),
      });
      if (res.ok) { const d = await res.json(); setSettings(d.settings); }
    } catch (e) { console.error('Failed to save alert settings', e); }
    finally { setSaving(false); }
  };

  const toggleType = (key) => save({ ...settings, [key]: !settings[key] });
  const allOn = settings && TYPE_LABELS.every(t => settings[t.key]);
  const allOff = settings && TYPE_LABELS.every(t => !settings[t.key]);
  const muteAll = () => save(Object.fromEntries(TYPE_LABELS.map(t => [t.key, false])));
  const enableAll = () => save(Object.fromEntries(TYPE_LABELS.map(t => [t.key, true])));

  if (!settings) return null;

  const label = allOff ? 'Client alerts off' : allOn ? 'Client alerts on' : 'Client alerts: some';

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        ref={btnRef}
        onClick={openMenu}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border shadow-sm transition-colors ${
          allOff
            ? 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
        }`}
        title="Control which notifications this search's clients receive"
      >
        <svg className={`w-3.5 h-3.5 ${allOff ? 'text-gray-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {allOff ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.73 21a2 2 0 01-3.46 0M18.63 13A17.89 17.89 0 0118 8M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14M18 8a6 6 0 00-9.33-5M1 1l22 22" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          )}
        </svg>
        {label}
        <svg className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && menuPos && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[100] bg-white border border-gray-200 rounded-lg shadow-lg py-1.5 w-64"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <p className="text-[11px] text-gray-500 px-3 pb-1.5">Notify this search's clients about…</p>
            {TYPE_LABELS.map(t => (
              <button key={t.key} onClick={() => toggleType(t.key)} disabled={saving}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                <span>{t.label}</span>
                <span className={`w-8 h-[18px] rounded-full relative transition-colors flex-shrink-0 ${settings[t.key] ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all ${settings[t.key] ? 'left-[18px]' : 'left-[2px]'}`} />
                </span>
              </button>
            ))}
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={allOff ? enableAll : muteAll}
              disabled={saving}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-60"
            >
              {allOff ? 'Turn all on' : 'Mute all for this search'}
            </button>
            <div className="border-t border-gray-100 my-1" />
            <label className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-gray-600 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={rememberDefault}
                onChange={(e) => setRememberDefault(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300" />
              Remember as my default for new searches
            </label>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientAlertsToggle;
